import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDateWithWeekday, formatTime } from '../utils/dateFormat';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Star Rating Input Component
const StarRatingInput = ({ rating, onChange }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none transition-transform hover:scale-110"
          data-testid={`star-${star}`}
        >
          <svg
            className={`w-8 h-8 ${
              star <= (hovered || rating) ? 'text-yellow-400' : 'text-gray-600'
            } transition-colors`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
      <span className="ml-2 text-light-text-secondary dark:text-silver/60 text-sm">
        {rating > 0 ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select rating'}
      </span>
    </div>
  );
};

// Review Modal Component
const ReviewModal = ({ appointment, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Minimum length for review text (characters)
  const MIN_REVIEW_LENGTH = 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    // Validate review text minimum length if provided
    const trimmedComment = comment.trim();
    if (trimmedComment.length > 0 && trimmedComment.length < MIN_REVIEW_LENGTH) {
      setError(`Review text must be at least ${MIN_REVIEW_LENGTH} characters. Please provide more detail about your experience.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(appointment.id, rating, comment);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-light-card dark:bg-obsidian border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full">
        <h3 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-2">Leave a Review</h3>
        <p className="text-light-text-secondary dark:text-silver/60 mb-6">
          Rate your appointment with {appointment.doctor_first_name ? `Dr. ${appointment.doctor_first_name} ${appointment.doctor_last_name}` : 'the doctor'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-3">
              Your Rating *
            </label>
            <StarRatingInput rating={rating} onChange={setRating} />
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
              Your Review (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this doctor..."
              className="input-field min-h-[120px] resize-none"
              rows={4}
              data-testid="review-comment-input"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-slate-400 dark:text-silver/40 text-xs">
                {comment.trim().length > 0 && comment.trim().length < MIN_REVIEW_LENGTH
                  ? `Minimum ${MIN_REVIEW_LENGTH} characters required`
                  : 'If provided, minimum 10 characters'}
              </p>
              <span className={`text-xs ${comment.trim().length > 0 && comment.trim().length < MIN_REVIEW_LENGTH ? 'text-red-400' : 'text-slate-400 dark:text-silver/40'}`}>
                {comment.trim().length} / {MIN_REVIEW_LENGTH} min
              </span>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-slate-500 dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || rating === 0}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-review-button"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Review'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Pagination Component
const Pagination = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;

  const { page, totalPages, hasNextPage, hasPrevPage, total } = pagination;

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-light-border dark:border-border">
      <p className="text-light-text-secondary dark:text-silver/60 text-sm font-mono">
        Page {page} of {totalPages} ({total} total)
      </p>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrevPage}
          className="px-3 py-2 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-slate-400 dark:hover:border-silver/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pagination-prev"
        >
          ← Prev
        </button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((pageNum, idx) => (
            pageNum === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 dark:text-silver/40">...</span>
            ) : (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`w-10 h-10 rounded-lg font-mono text-sm transition-colors ${
                  pageNum === page
                    ? 'bg-light-text dark:bg-pure-white text-white dark:text-obsidian font-bold'
                    : 'border border-light-border dark:border-border hover:border-slate-400 dark:hover:border-silver/40'
                }`}
                data-testid={`pagination-page-${pageNum}`}
              >
                {pageNum}
              </button>
            )
          ))}
        </div>

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage}
          className="px-3 py-2 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-slate-400 dark:hover:border-silver/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pagination-next"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const PatientAppointments = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewingAppointment, setReviewingAppointment] = useState(null);
  const [reviewedAppointments, setReviewedAppointments] = useState(new Set());
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState(''); // Filter: from date
  const [dateTo, setDateTo] = useState(''); // Filter: to date
  const itemsPerPage = 5; // Show 5 appointments per page for easier testing

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchAppointments();
    fetchPendingReviews();
  }, [user, token, sortOrder, currentPage, dateFrom, dateTo]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      // Build query string with filters
      const params = new URLSearchParams({
        sortOrder,
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      // Add date filters if set
      if (dateFrom) {
        params.append('from', `${dateFrom}T00:00:00.000Z`);
      }
      if (dateTo) {
        params.append('to', `${dateTo}T23:59:59.999Z`);
      }

      const response = await fetch(`${API_URL}/appointments?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();
      setAppointments(data.appointments || []);
      setPagination(data.pagination || null);

      // If we're on a page that no longer exists (due to data changes), go to last valid page
      if (data.pagination && currentPage > data.pagination.totalPages && data.pagination.totalPages > 0) {
        setCurrentPage(data.pagination.totalPages);
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of appointments section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Build export query string with filters (same as list view)
      const params = new URLSearchParams({ sortOrder });
      // Add date filters if set - export should respect current filters
      if (dateFrom) {
        params.append('from', `${dateFrom}T00:00:00.000Z`);
      }
      if (dateTo) {
        params.append('to', `${dateTo}T23:59:59.999Z`);
      }
      console.log('[EXPORT] Exporting with filters:', { dateFrom, dateTo, sortOrder });

      const response = await fetch(`${API_URL}/appointments/export/csv?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export appointments');
      }

      // Get the CSV content as text
      const csvContent = await response.text();

      // Create a Blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'appointments_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('[EXPORT] CSV downloaded successfully');
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export appointments. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const fetchPendingReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/reviews/patient`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Create a set of appointment IDs that have been reviewed
        const reviewedIds = new Set();
        // We don't have appointment_id in the reviews/patient response,
        // so we need to fetch pending reviews instead
        const pendingResponse = await fetch(`${API_URL}/reviews/pending`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (pendingResponse.ok) {
          const pendingData = await pendingResponse.json();
          const pendingIds = new Set(pendingData.pendingReviews?.map(p => p.appointmentId) || []);
          // All appointments that are NOT in pending reviews have been reviewed
          // We'll track which ones have been reviewed by checking which ones are NOT pending
          setReviewedAppointments(reviewedIds);
        }
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
    }
  };

  const handleSubmitReview = async (appointmentId, rating, comment) => {
    const response = await fetch(`${API_URL}/reviews`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        appointmentId,
        rating,
        comment: comment || null
      })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to submit review');
    }

    // Mark this appointment as reviewed
    setReviewedAppointments(prev => new Set([...prev, appointmentId]));

    // Refresh appointments
    await fetchAppointments();
  };

  const canReview = (appointment) => {
    return (
      (appointment.status === 'completed' || appointment.status === 'confirmed') &&
      !reviewedAppointments.has(appointment.id)
    );
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    return (
      <span className={`px-2 py-1 text-xs font-mono uppercase tracking-widest rounded border ${statusStyles[status] || statusStyles.pending}`}>
        {status}
      </span>
    );
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen pb-16">
      {/* Review Modal */}
      {reviewingAppointment && (
        <ReviewModal
          appointment={reviewingAppointment}
          onClose={() => setReviewingAppointment(null)}
          onSubmit={handleSubmitReview}
        />
      )}

      {/* Header */}
      <section className="pt-8 pb-8 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl italic text-light-text dark:text-pure-white mb-2">My Appointments</h1>
              <p className="text-light-text-secondary dark:text-silver/60">View and manage your upcoming and past appointments</p>
            </div>

            {/* Filter Controls, Sort and Export */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              {/* Date Range Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="filter-from" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                    From:
                  </label>
                  <input
                    id="filter-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filter changes
                    }}
                    className="bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-lg px-3 py-2 font-mono text-sm text-light-text dark:text-pure-white focus:outline-none focus:border-slate-400 dark:focus:border-silver/40 transition-colors"
                    data-testid="filter-date-from"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="filter-to" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                    To:
                  </label>
                  <input
                    id="filter-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1); // Reset to page 1 when filter changes
                    }}
                    className="bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-lg px-3 py-2 font-mono text-sm text-light-text dark:text-pure-white focus:outline-none focus:border-slate-400 dark:focus:border-silver/40 transition-colors"
                    data-testid="filter-date-to"
                  />
                </div>
                {/* Clear Filters Button */}
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setCurrentPage(1);
                    }}
                    className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver text-xs font-mono uppercase tracking-widest transition-colors"
                    data-testid="clear-filters-button"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Sort Controls */}
              <div className="flex items-center gap-2">
                <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  Sort:
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when sort order changes
                  }}
                  className="bg-white dark:bg-obsidian border border-light-border dark:border-border rounded-lg px-3 py-2 font-mono text-sm text-light-text dark:text-pure-white focus:outline-none focus:border-slate-400 dark:focus:border-silver/40 transition-colors"
                  data-testid="sort-order-select"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>

              {/* Export to CSV Button - exports filtered results */}
              <button
                onClick={handleExportCSV}
                disabled={exporting || loading || appointments.length === 0}
                className="flex items-center gap-2 px-4 py-2 border border-light-border dark:border-border rounded-lg font-mono text-xs uppercase tracking-widest hover:border-slate-400 dark:hover:border-silver/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="export-csv-button"
                title={(dateFrom || dateTo) ? 'Export filtered appointments to CSV' : 'Export all appointments to CSV'}
              >
                {exporting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {(dateFrom || dateTo) ? 'Export Filtered' : 'Export CSV'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Appointments List */}
      <section className="px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
            </div>
          ) : error ? (
            <div className="card text-center py-8">
              <p className="text-red-400">{error}</p>
              <button
                onClick={fetchAppointments}
                className="mt-4 btn-primary"
              >
                Try Again
              </button>
            </div>
          ) : appointments.length === 0 ? (
            <div className="card text-center py-12">
              <svg className="w-16 h-16 text-silver/30 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {(dateFrom || dateTo) ? (
                <>
                  <h3 className="font-display text-xl italic text-light-text dark:text-pure-white mb-2">No Matching Appointments</h3>
                  <p className="text-light-text-secondary dark:text-silver/60 mb-6">
                    No appointments found for the selected date range
                    {dateFrom && dateTo && ` (${dateFrom} to ${dateTo})`}
                    {dateFrom && !dateTo && ` (from ${dateFrom})`}
                    {!dateFrom && dateTo && ` (until ${dateTo})`}
                    .
                  </p>
                  <button
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setCurrentPage(1);
                    }}
                    className="btn-primary inline-block"
                  >
                    Clear Filters
                  </button>
                </>
              ) : (
                <>
                  <h3 className="font-display text-xl italic text-light-text dark:text-pure-white mb-2">No Appointments Yet</h3>
                  <p className="text-light-text-secondary dark:text-silver/60 mb-6">You haven't booked any appointments yet.</p>
                  <Link to="/marketplace" className="btn-primary inline-block">
                    Find a Doctor
                  </Link>
                </>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="space-y-4">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="border border-light-border dark:border-border rounded-xl p-4" data-testid="appointment-card">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Doctor/Service Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-light-text dark:text-pure-white">
                            {appointment.doctor_first_name
                              ? `Dr. ${appointment.doctor_first_name} ${appointment.doctor_last_name}`
                              : 'Doctor'}
                          </h3>
                          {getStatusBadge(appointment.status)}
                        </div>
                        <p className="text-light-text-secondary dark:text-silver/60 text-sm mb-1">{appointment.service_name}</p>
                        <p className="text-slate-400 dark:text-silver/40 text-sm">
                          {formatDateWithWeekday(appointment.start_time)}
                          {' at '}
                          {formatTime(appointment.start_time)}
                        </p>
                        {/* Show appointment ID for easier testing */}
                        <p className="text-slate-300 dark:text-silver/30 text-xs font-mono mt-1">
                          ID: {appointment.id}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3">
                        {canReview(appointment) && (
                          <button
                            onClick={() => setReviewingAppointment(appointment)}
                            className="btn-primary text-sm"
                            data-testid="leave-review-button"
                          >
                            Leave Review
                          </button>
                        )}
                        {reviewedAppointments.has(appointment.id) && (
                          <span className="text-green-400 text-sm flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Reviewed
                          </span>
                        )}
                        {appointment.doctor_id && (
                          <Link
                            to={`/doctor/${appointment.doctor_id}`}
                            className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver text-sm transition-smooth"
                          >
                            View Doctor
                          </Link>
                        )}
                        <Link
                          to={`/patient/appointments/${appointment.id}`}
                          className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver text-sm transition-smooth"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PatientAppointments;
